import React, { useState, useEffect, useRef } from 'react'
import { ID } from "appwrite"
// import { storage, account } from '../appwrite/config'
import { Client, Storage, Account } from 'appwrite';
import { atom, useAtom } from 'jotai'
import { styled } from '@mui/material/styles';
import {
    UserSession,
    AdminUser
} from '../utils'
import {
    Box,
    Stack,
    Typography,
    Divider,
    ListItemText,
    ListItemIcon,
    Chip,
    Button,
    Tooltip,
    IconButton,
    Badge,
    List,
    ListItem,
    ListItemButton,
    CssBaseline,
    LinearProgress,
    Snackbar,
    Alert,
    Pagination,
    Paper
} from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import PlaylistRemoveOutlinedIcon from '@mui/icons-material/PlaylistRemoveOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';


const client = new Client()
    .setEndpoint(process.env.REACT_APP_ENDPOINT)
    .setProject(process.env.REACT_APP_PROJECT_ID);

const account = new Account(client);
const storage = new Storage(client);

const isLoading = atom(0)

export { isLoading }


/**
 * @FileUpload
 *
 * @param {String} type Optional. List of file extensions to accept; if not specified, accepts any.
 * @param {Boolean} multiple Optional. Allow multiple file selections (currently incongruous with directory selection).
 * @param {Boolean} directory Optional. Allow directory selection (currently incongruous with multiple files).
 * @param {Boolean} scroll Optional. Enable horizontal scroll for filenames that run outside the file box. Default is ellipsis.
 * @param {Integer} filesLength Optional. Number of files to display per page (default is 4).
 * @param {React.State} refresh Optional. Force-refresh the FileUpload component when this state changes. This is useful for wiping the file list when something changes on your page.
 * @param {Boolean} verticalMode Optional. Display the file list vertically (mobile or small screen res). Default is horizontal.
 * @param {Boolean} disabled Optional. Disable the FileUpload component.
 * 
 * @returns {JSX.Element} Rendered FileUpload component.
*/
/**
 * @TODO
 * - Add support for guest usage
 * - Support for simultaneous file & directory selection (toggle between input types?)
 * - Support for maintaining directory structure on process output (is this even possible?)
 * - Preview window for single image file
 * - File previews on filename hover (when possible; convert PDFs to images)
 */
export default function FileUpload({ type, multiple, directory, scroll, filesLength, refresh, verticalMode, disabled }) {
    const [userSession, setUserSession] = useAtom(UserSession);
    const [loading, setLoading] = useAtom(isLoading);

    const extensions = type ? type.split(' ') : null

    const [files, setFiles] = useState([])
    const [selectedFiles, setSelectedFiles] = useState([])

    const [currentPage, setCurrentPage] = useState(1)
    const [filesPerPage, setFilesPerPage] = useState(filesLength ? filesLength : 4)

    const [isHovered, setIsHovered] = useState(-1)
    const [open, setOpen] = useState(false)

    const [showBackdrop, setShowBackdrop] = useState(false)
    const dropRef = useRef(null);

    // Resizing components consistent with screen width
    const resize = { lg: 1200, md: 900, sm: 600 }


    var pageArray = []
    for (let i = 1; i <= Math.ceil(files.length / filesPerPage); i++) {
        pageArray.push(i)
    }

    // Clear any pre-existing files on load
    useEffect(() => {
        handleStorageRemoveAll()
    }, [])

    useEffect(() => {
        if (refresh) handleRemoveAll();
    }, [refresh])

    const handleStorageUpload = (event, dropped) => {
        event.preventDefault()
        setShowBackdrop(false)
        const files = dropped ? event.dataTransfer.files : event.target.files

        // Filter files based on accepted extensions (ignore if no extensions are specified)
        const newFiles = multiple ? files : [files[0]];
        const filteredFiles = type
            ? Array.from(newFiles).filter((file) => {
                const fileExtension = file.name.split('.').pop().toLowerCase();
                const isValid = extensions.map((ext) => ext.toLowerCase()).includes(fileExtension);

                if (!isValid) setOpen(`Cannot upload file '${file.name}' with extension '${fileExtension}'.`);
                return isValid;
            })
            : newFiles;

        // Only update the selected files if there are valid new files (i.e. unaccepted extensions won't be added)
        if (filteredFiles.length > 0) {
            // Write new files to storage in parallel
            Promise.all(filteredFiles.map(async (file) => {
                setLoading(filteredFiles.length);

                const bucketId = `${userSession.$id}_input`;
                const fileId = ID.unique();
                let fileData = file

                const uploadUrl = `${process.env.REACT_APP_ENDPOINT}/v1/storage/buckets/${bucketId}/files/${fileId}?project=${process.env.REACT_APP_PROJECT_ID}`;

                const xhr = new XMLHttpRequest();
                const formData = new FormData();
                formData.append('file', file);
                xhr.open('PUT', uploadUrl, true);

                file.$id = fileId;
                file.progress = 0;
                setFiles((prevFiles) => {
                    return multiple ? [...prevFiles, file] : [file];
                });

                storage.createFile(bucketId, fileId, file, [], (progress) => {
                    // Not a viable method, since it only updates every 5 MB
                }).catch((err) => {
                    console.error(`Upload failed: ${err.message}`);
                });

                // Track loading progress
                xhr.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        fileData.progress = ((event.loaded / event.total) * 100).toFixed(0);
                        setFiles((prevFiles) => {
                            return prevFiles.map(f => f.$id === fileId ? fileData : f);
                        });
                    }
                };

                xhr.onerror = () => console.warn('Upload failed due to a network error; this is expected behavior due to how files are uploaded, and can be ignored.');
                // Use state change to flag loading as finished
                xhr.onreadystatechange = () => {
                    if (xhr.readyState === 4) {
                        setLoading((prevLoading) => prevLoading - 1);
                    }
                };
                xhr.send(formData);
            })).then((createdFiles) => {
                setSelectedFiles(createdFiles);
            }).catch((err) => {
                // console.error(err);
            });
        }
    }

    // Check for mouse hovering during drag and drop (fixes an onDropOver/onDragLeave firing bug in Chrome)
    const handleDragOver = (event) => {
        event.preventDefault()
        const isHovering = dropRef.current?.contains(document.elementFromPoint(event.clientX, event.clientY));
        if (isHovering && !showBackdrop) setShowBackdrop(true);
    }

    const handleDragLeave = (event) => {
        const isHovering = dropRef.current?.contains(document.elementFromPoint(event.clientX, event.clientY));
        if (!isHovering) setShowBackdrop(false);
    }

    // Remove files from both UI and storage
    const handleRemoveFile = (index) => {
        handleStorageRemove(files[index]);

        const updatedFiles = [...files];
        updatedFiles.splice(index, 1);
        setFiles(updatedFiles);

        // Update user page if the page number decreases after removing a file (so we don't get left on a null page)
        const totalPages = Math.ceil(updatedFiles.length / filesPerPage);

        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }

        // Reset current page to 1 if there are no files left
        if (updatedFiles.length === 0) {
            setCurrentPage(1);
        }
    }

    const handleRemoveAll = () => {
        handleStorageRemoveAll()
        setFiles([])
        setCurrentPage(1)
    }

    // Remove files from storage ONLY
    const handleStorageRemove = (file) => {
        storage.deleteFile(userSession ? `${userSession.$id}_input` : "temp_input", file.$id)
            .catch((error) => {
                console.error(error)
            })
    }

    const handleStorageRemoveAll = () => {
        storage.listFiles(userSession ? `${userSession.$id}_input` : "temp_input")
            .then((response) => {
                response.files.forEach((file) => {
                    if (file) storage.deleteFile(userSession ? `${userSession.$id}_input` : "temp_input", file.$id);
                })
            }).catch((error) => {
                console.error(error)
            })
    }

    function formatFileSize(size, p) {
        if (size < 1024) {
            return `${size} bytes`;
        } else if (size < 1024 * 1024) {
            return `${(size / 1024).toFixed(p || 1)} KB`;
        } else if (size < 1024 * 1024 * 1024) {
            return `${(size / (1024 * 1024)).toFixed(p || 1)} MB`;
        } else {
            return `${(size / (1024 * 1024 * 1024)).toFixed(p || 1)} GB`;
        }
    }

    // Figure out which files should be shown relative to current page
    const indexOfLastFile = currentPage * filesPerPage
    const indexOfFirstFile = indexOfLastFile - filesPerPage
    const currentFiles = files.slice(indexOfFirstFile, indexOfLastFile)


    // Allow file upload on an MUI Button component
    const VisuallyHiddenInput = styled('input') ({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    const tooltipFromMime = (mimeType) => {
        const ext = mimeType.split('.').pop().toLowerCase()
        
        // Map of extensions to their descriptions
        const descMap = {
            'xlsx,xlsm,xls': 'Excel sheet',
            'doc,docx': 'Word document', 
            'odt,txt': 'Text document',
            'zip': 'Zip archive',
            'jpg,png,jpeg,gif': 'Image',
            'py,html,css,js,jsx,json,yml,yaml': 'Code file'
        }

        for (const [extensions, desc] of Object.entries(descMap)) {
            if (extensions.split(',').includes(ext)) return desc
        }

        return ext.toUpperCase()
    }

    const iconFromMime = (mimeType) => {
        const ext = mimeType.split('.').pop().toLowerCase()
        
        // Map of extensions to their icon classes
        const iconMap = {
            'pdf': 'bi-filetype-pdf',
            'xlsx,xls,xlsm': 'bi-file-earmark-excel', 
            'csv': 'bi-file-earmark-spreadsheet',
            'docx,doc': 'bi-file-earmark-word',
            'odt,txt': 'bi-file-earmark-text',
            'zip': 'bi-file-earmark-zip',
            'jpg,png,jpeg,gif': 'bi-file-earmark-image',
            'py,html,css,js,jsx,json,yml,yaml': 'bi-file-earmark-code'
        }

        for (const [extensions, icon] of Object.entries(iconMap)) {
            if (extensions.split(',').includes(ext)) return icon
        }
        
        return 'bi-file-earmark'
    }

    return (<>
        <Stack
            direction={window.innerWidth <= resize.sm || verticalMode ? "column" : "row"}
            ref={dropRef}
            key={refresh || undefined}
            sx={{
                position: 'relative',
                width: '100%',
                minHeight: '9em',
                background: 'rgba(180, 180, 180, 0.1)',
                p: '1em',
                alignItems: 'center',
                borderRadius: '5px',
                overflow: 'hidden'
            }}
            className="file-upload file-drop"
            onDragOver={() => {
                if (!disabled) handleDragOver();
            }}
            onDragLeave={() => {
                if (!disabled) handleDragLeave();
            }}
            onDrop={(event) => {
                if (!disabled) handleStorageUpload(event, true);
            }}
        >
            {/* Disabled overlay */}
            <Paper
                elevation={1}
                sx={{
                    display: disabled ? 'flex' : 'none',
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(25, 25, 25, 0.5)',
                    zIndex: 1
                }}
            />

            {/* Drag-and-drop backdrop */}
            <Paper
                elevation={1}
                sx={{
                    display: showBackdrop ? 'flex' : 'none',
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(180, 180, 180, 0.25)',
                    animation: 'fadeIn 0.1s ease-in',
                    '@keyframes fadeIn': {
                        '0%': {
                        opacity: '0',
                        },
                        '100%': {
                        opacity: '1',
                        },
                    },
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: '2.0em',
                        color: 'var(--primary-text-color)',
                        textShadow: '1px 1px 20px rgba(0, 0, 0, 0.75)',
                        userSelect: 'none'
                    }}
                >
                    Drop files here
                </Typography>
            </Paper>

            {/* Upload button */}
            <Button
                component='label'
                variant='outlined'
                sx={{
                    width: window.innerWidth <= resize.sm || verticalMode ? '100%' : '20em',
                    minWidth: '20em',
                    minHeight: '8em',
                    background: 'rgba(100, 100, 100, 0.1)',
                    p: '1em',
                    mr: window.innerWidth <= resize.sm || verticalMode ? 0 : '1em',
                    alignItems: 'center',
                    textAlign: 'left',
                    textTransform: 'none'
                }}
            >
                <UploadFileOutlinedIcon sx={{ fontSize: '4em', color: '#ccc', ml: '0.1em', mr: '0.3em' }} />
                <Stack>
                    <Typography variant="h6" gutterBottom >
                        Drag and drop or browse files
                    </Typography>
                    {extensions && <Typography variant="body2" color="text.secondary">
                        {extensions.join(', ').toUpperCase()}
                    </Typography>}
                </Stack>
                <VisuallyHiddenInput
                    type="file"
                    id="file-input"
                    onChange={handleStorageUpload}
                    multiple={true}
                />
            </Button>

            {/* Files list */}
            <Stack
                width={window.innerWidth <= resize.sm || verticalMode ? '100%' : 'calc(100% - 15em)'}
                sx={{ mt: window.innerWidth <= resize.sm || verticalMode ? '1em' : 0 }}
                className="select-files-list"
            >
                {files.length > 0 ? currentFiles.map((file, index) => (<Stack
                    key={file.$id}
                    label={file.name}
                    direction="column"
                    sx={{
                        width: '100%',
                        background: `rgba(100, 100, 100, ${isHovered === index ? 0.2 : 0.1})`,
                        transition: 'background 0.3s ease',
                        borderRadius: '5px',
                        p: '0.25em',
                        mb: '2px',
                        pl: '0.75em',
                        pr: '0.5em',
                    }}
                    onMouseEnter={() => setIsHovered(index)}
                    onMouseLeave={() => setIsHovered(-1)}
                >
                    <Stack
                        key={file.$id}
                        label={file.name}
                        direction="row"
                        spacing={1}
                        sx={{
                            width: '100%',
                            borderRadius: '5px',
                            alignItems: 'center'
                        }}
                    >
                        {/* File icon */}
                        <Tooltip title={tooltipFromMime(file.name)}>
                            <Typography
                                variant="body2"
                                gutterBottom
                                sx={{ fontSize: '1.1em', color: '#ccc' }}
                                className={`file-icon bi ${iconFromMime(file.name)}`}
                                key={file.$id}
                            />
                        </Tooltip>

                        {/* File name */}
                        <Tooltip title={file.name}>
                            <Typography
                                className="file-name"
                                flexGrow={1}
                                sx={{
                                    pl: '0.5em',
                                    fontSize: '1em',
                                    whiteSpace: 'nowrap',
                                    textOverflow: scroll ? 'clip' : 'ellipsis',
                                    overflow: scroll ? 'auto' : 'hidden'
                                }}
                            >
                                {file.name}
                            </Typography>
                        </Tooltip>

                        {/* File size */}
                        <Tooltip title={formatFileSize(file.size, 2)}>
                            <Typography
                                variant="body2"
                                className="file-size"
                                key={"size-" + file.$id}
                                sx={{ fontSize: '0.8em', whiteSpace: 'nowrap', color: '#ccc' }}
                            >
                                {formatFileSize(file.size)}
                            </Typography>
                        </Tooltip>

                        {/* Remove file button */}
                        {(window.innerWidth <= resize.sm || verticalMode || isHovered === index) && (
                            <Tooltip title="Remove file">
                                <Button
                                    className="file-remove"
                                    sx={{ width: "1em", height: "1em", cursor: 'pointer' }}
                                    onClick={() => handleRemoveFile(files.indexOf(file))}
                                >
                                    <CloseOutlinedIcon key={"remove-" + file.$id} sx={{ width: '0.75em' }} />
                                </Button>
                            </Tooltip>
                        )}
                    </Stack>

                    {/* File progress */}
                    {file.progress < 100 && <Box
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress variant="determinate" value={Number(file.progress)} />
                        </Box>
                        <Box sx={{ minWidth: 30 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {`${Math.round(file.progress)}%`}
                            </Typography>
                        </Box>
                    </Box>}
                </Stack>)) :
                handleStorageRemoveAll && <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                    sx={{ alignItems: 'center' }}
                >
                    {/* <InsertDriveFileOutlinedIcon sx={{ fontSize: '2.5em', color: '#ccc' }} /> */}
                    <Typography variant="body1" sx={{ fontSize: '1.2em', color: '#ccc', userSelect: 'none' }}>
                        <i>{`No file${multiple ? 's' : ''} selected`}</i>
                    </Typography>
                </Stack>}

                {/* File controls */}
                {files.length > filesPerPage && <Stack
                        spacing={1}
                        direction="row"
                        justifyContent="center"
                        sx={{ alignItems: 'center', mt: '0.2em' }}
                    >
                        <Pagination 
                            count={Math.ceil(files.length / filesPerPage)} 
                            shape="rounded" 
                            siblingCount={window.innerWidth <= resize.sm || verticalMode ? 0 : 1} 
                            onChange={(event, page) => setCurrentPage(page)}
                        />
                        <Tooltip title="Clear all files">
                            <Button sx={{ width: "2.5em", height: "2.4em" }} onClick={() => handleRemoveAll()}>
                                <PlaylistRemoveOutlinedIcon sx={{ cursor: 'pointer', width: '0.9em' }} />
                            </Button>
                        </Tooltip>
                    </Stack>}
            </Stack>
        </Stack>
        <Snackbar
            open={Boolean(open)}
            onClose={() => setOpen(false)}
            autoHideDuration={5000}
            key="snackbar"
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert severity="error">{open}</Alert>
        </Snackbar>
    </>)
}